import { useEffect, useMemo, useState, useContext } from "react";
import { useFirestoreGeneralOrderBy } from "../../firebase/useFirestore";
import { db, timestamp, bucket } from "../../firebase/config";
import uuid from "react-uuid";
import { client } from "../../hooks/Client";
import { SavedIcon } from "../../StateManagment/SavedIcon";
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import AddItemTableRow from "../common/AddItemTableRow.jsx";
import Modal from "../common/Modal.jsx";
import DragIndicatorOutlinedIcon from "@mui/icons-material/DragIndicatorOutlined";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import ReadOnlyGuard from "../common/ReadOnlyGuard";

const TaskImages = ({ task }) => {
  // Context
  const [, setSaved] = useContext(SavedIcon);

  // State
  const [showAddContainer, setShowAddContainer] = useState(false);
  const [uploading, setUploading] = useState(false);

  // Uploaded URLs waiting to be saved to Firestore
  const [pendingUrls, setPendingUrls] = useState([]); // string[]
  const [pendingMeta, setPendingMeta] = useState([]); // {name, type, size}[]

  // Simple overall progress (0-100)
  const [loading, setLoading] = useState(0);

  // For drag & drop
  const [dragArray, setDragArray] = useState([]);

  // Firestore
  const photos = useFirestoreGeneralOrderBy(
    "TaskImages",
    "TaskID",
    task?.ID || "",
    "Position",
    "asc"
  );

  useEffect(() => {
    setDragArray(Array.isArray(photos) ? photos : []);
  }, [photos]);

  const nextBasePosition = useMemo(() => {
    return (Array.isArray(photos) ? photos.length : 0) + 1;
  }, [photos]);

  const isImage = (type = "") => (type || "").split("/").includes("image");

  // ----------------------------
  // Upload helpers (multi-file)
  // ----------------------------

  const uploadOneFile = (file, onProgress) => {
    return new Promise((resolve, reject) => {
      if (!file) return resolve(null);

      // Avoid collisions: put uploads under a folder and prefix with uuid
      const safeName = `${uuid()}-${file.name}`;
      const storageRef = bucket.ref(`task-images/${client}/${safeName}`);
      const uploadTask = storageRef.put(file);

      uploadTask.on(
        "state_changed",
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          onProgress && onProgress(progress);
        },
        (err) => reject(err),
        async () => {
          try {
            const downloadURL = await uploadTask.snapshot.ref.getDownloadURL();
            resolve(downloadURL);
          } catch (e) {
            reject(e);
          }
        }
      );
    });
  };

  const mediaHandler = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    setUploading(true);
    setLoading(0);

    try {
      // Upload sequentially (predictable progress)
      const urls = [];
      const metas = [];

      for (let i = 0; i < files.length; i++) {
        const file = files[i];

        // map per-file progress into overall progress
        const base = (i / files.length) * 100;
        const portion = 100 / files.length;

        const url = await uploadOneFile(file, (p) => {
          setLoading(Math.min(100, Math.round(base + (p / 100) * portion)));
        });

        if (url) {
          urls.push(url);
          metas.push({ name: file.name, type: file.type, size: file.size });
        }
      }

      setPendingUrls((prev) => [...prev, ...urls]);
      setPendingMeta((prev) => [...prev, ...metas]);
      setLoading(100);
    } catch (err) {
      console.error("Upload error:", err);
      alert(err?.message || "Upload mislukt");
      setLoading(0);
    } finally {
      setUploading(false);
      // reset input so selecting the same file again triggers onChange
      e.target.value = "";
    }
  };

  // Save uploaded URLs to Firestore
  const addPhotos = async () => {
    if (!task?.ID) return;
    if (!pendingUrls.length) return;

    try {
      // Create docs in the order they were uploaded
      const writes = pendingUrls.map((url, idx) => {
        const meta = pendingMeta[idx] || {};
        return db.collection("TaskImages").doc().set({
          TaskID: task.ID,
          Url: url,
          Type: meta.type || "image/*",
          FileName: meta.name || "",
          FileSize: meta.size || null,
          ID: uuid(),
          Timestamp: timestamp,
          CompagnyID: client,
          Position: nextBasePosition + idx,
        });
      });

      await Promise.all(writes);

      setPendingUrls([]);
      setPendingMeta([]);
      setLoading(0);
      setShowAddContainer(false);
      setSaved("flex");
    } catch (err) {
      console.error("Error saving TaskImages:", err);
      alert(err?.message || "Opslaan mislukt");
    }
  };

  const deletePhoto = async (e) => {
    const docid = e.target.getAttribute("data-docid");
    if (!docid) return;

    try {
      await db.collection("TaskImages").doc(docid).delete();
      setSaved("flex");
    } catch (err) {
      console.error("Error deleting TaskImage:", err);
      alert(err?.message || "Verwijderen mislukt");
    }
  };

  // DRAG & DROP reorder
  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const items = Array.from(dragArray);
    const [movedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, movedItem);

    setDragArray(items);

    items.forEach((item, idx) => {
      db.collection("TaskImages").doc(item.docid).update({ Position: idx + 1 });
    });

    setSaved("flex");
  };

  return (
    <div className="task-notes-container">
      <h3>Foto&apos;s</h3>

      <ReadOnlyGuard edit="tasks.edit">
      {(canEdit) => (
      <>
      {/* Existing photos */}
      {dragArray && dragArray.length > 0 ? (
        <table>
          <thead>
            <tr>
              <th></th>
              <th>FOTO</th>
              <th>TYPE</th>
              <th>VERWIJDEREN</th>
            </tr>
          </thead>

          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="taskimages" direction="vertical">
              {(provided) => (
                <tbody ref={provided.innerRef} {...provided.droppableProps}>
                  {dragArray.map((photo, index) => (
                    <Draggable
                      key={photo.ID}
                      draggableId={photo.ID}
                      index={index}
                    >
                      {(provided) => (
                        <tr ref={provided.innerRef} {...provided.draggableProps}>
                          <td {...provided.dragHandleProps}>
                            <DragIndicatorOutlinedIcon />
                          </td>

                          <td>
                            {isImage(photo.Type) ? (
                              <img
                                src={photo.Url}
                                alt=""
                                style={{
                                  width: "90px",
                                  height: "60px",
                                  objectFit: "cover",
                                  borderRadius: "6px",
                                }}
                              />
                            ) : (
                              <a href={photo.Url} target="_blank" rel="noreferrer">
                                Open bestand
                              </a>
                            )}
                          </td>

                          <td>
                            <p>{photo.Type || "Onbekend"}</p>
                          </td>

                          <td>
                            {canEdit && (
                              <DeleteOutlinedIcon
                                className="table-delete-icon"
                                data-docid={photo.docid}
                                onClick={deletePhoto}
                              />
                            )}
                          </td>
                        </tr>
                      )}
                    </Draggable>
                  ))}

                  {provided.placeholder}
                </tbody>
              )}
            </Droppable>
          </DragDropContext>
        </table>
      ) : (
        <p>Geen foto&apos;s toegevoegd.</p>
      )}

      {/* Add */}
      {canEdit && (
        <AddItemTableRow
          toolTipContent={"Foto toevoegen"}
          onClick={() => setShowAddContainer(true)}
        />
      )}

      <Modal
        modalOpen={showAddContainer}
        setModalOpen={setShowAddContainer}
        pageHeader={"Foto toevoegen"}
        hideConfirm={true}
        content={
          <>
            <div
              className="add-options-container-item-container"
              id="storytelling-file-upload-container"
            >
              {loading > 0 && loading < 100 && (
                <div className="file-upload-loading-container">
                  <div
                    className="file-upload-loading-bar"
                    style={{ width: `${loading}%` }}
                  />
                  <p>Bezig met uploaden... {Math.round(loading)}%</p>
                </div>
              )}

              {loading === 100 && pendingUrls.length > 0 && (
                <div className="file-upload-loading-container">
                  <div
                    className="file-upload-loading-bar"
                    style={{ width: "100%", backgroundColor: "green" }}
                  />
                  <p>Upload compleet! ({pendingUrls.length} bestand(en))</p>
                </div>
              )}

              {loading === 0 && pendingUrls.length === 0 && (
                <p>
                  Kies één of meerdere foto&apos;s. Je kunt ze ook één voor één
                  toevoegen — ze worden verzameld en samen opgeslagen.
                </p>
              )}

              <input
                type="file"
                multiple
                accept="image/*"
                onChange={mediaHandler}
                disabled={uploading}
              />

              {/* Preview of pending uploads */}
              {pendingUrls.length > 0 && (
                <>
                <p>
                  {pendingUrls.length}{" "}
                  {pendingUrls.length === 1 ? "foto" : "foto's"} klaar om op te
                  slaan. Tik hierboven opnieuw om er meer toe te voegen.
                </p>
                <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginTop: "10px" }}>
                  {pendingUrls.map((url) => (
                    <img
                      key={url}
                      src={url}
                      alt=""
                      style={{
                        width: "70px",
                        height: "50px",
                        objectFit: "cover",
                        borderRadius: "6px",
                      }}
                    />
                  ))}
                </div>
                </>
              )}
            </div>

            <button
              className="button-simple"
              onClick={addPhotos}
              disabled={uploading || pendingUrls.length === 0}
            >
              Opslaan
            </button>
          </>
        }
      />
      </>
      )}
      </ReadOnlyGuard>
    </div>
  );
};

export default TaskImages;
